import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { formatGBP, todayISO, formatUKDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Check, X, RotateCw, CircleDollarSign, Play, GripVertical, MapPin, Phone, Pause } from "lucide-react";
import { Link } from "react-router-dom";

export default function DailyRoute() {
  const [jobs, setJobs] = useState([]);
  const [routeMode, setRouteMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    const { data } = await api.get("/jobs", { params: { date_from: todayISO(), date_to: todayISO() } });
    setJobs([...data].sort((a, b) => (a.route_order || 0) - (b.route_order || 0)));
  };
  useEffect(() => { load(); }, []);

  const completed = jobs.filter((j) => j.status === "completed").length;
  const total = jobs.length;
  const earned = useMemo(() => jobs.filter((j) => ["paid_cash", "paid_online"].includes(j.payment_status)).reduce((a, b) => a + (b.price || 0), 0), [jobs]);

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = async (idx) => {
    if (dragIndex === null || dragIndex === idx) return;
    const next = [...jobs];
    const [m] = next.splice(dragIndex, 1);
    next.splice(idx, 0, m);
    next.forEach((j, i) => j.route_order = i);
    setJobs(next);
    setDragIndex(null);
    await api.post("/jobs/reorder", { items: next.map((j) => ({ job_id: j.id, route_order: j.route_order })) });
  };

  const act = async (id, fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="route-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight font-bold">Today's route</h1>
          <div className="text-sm text-muted-foreground">{formatUKDate(todayISO())}</div>
        </div>
        {!routeMode ? (
          <button onClick={() => setRouteMode(true)} className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium" data-testid="start-route-button">
            <Play className="h-4 w-4" /> Start route
          </button>
        ) : (
          <button onClick={() => setRouteMode(false)} className="inline-flex items-center gap-2 rounded-md bg-secondary text-secondary-foreground px-4 py-2 text-sm font-medium" data-testid="pause-route-button">
            <Pause className="h-4 w-4" /> Pause
          </button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <StatCell label="Progress" value={`${completed}/${total}`} />
        <StatCell label="Money in" value={formatGBP(earned)} />
        <StatCell label="To do" value={total - completed} />
      </div>

      {jobs.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          Nothing scheduled today. <Link to="/app/jobs" className="text-primary underline">Add a job</Link>.
        </div>
      ) : (
        <ul className="mt-5 space-y-2" data-testid="route-list">
          {jobs.map((j, i) => (
            <li
              key={j.id}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(i)}
              className={`rounded-xl border bg-card p-4 flex items-start gap-3 ${j.status === "completed" ? "opacity-60" : ""}`}
              data-testid={`route-item-${j.id}`}
            >
              <div className="shrink-0 flex flex-col items-center gap-2">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">{i + 1}</div>
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate">{j.customer_name}</div>
                  <div className="font-semibold">{formatGBP(j.price)}</div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{j.customer_address} · {j.customer_postcode}</div>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={j.status} />
                  <StatusBadge status={j.payment_status} />
                </div>
                {(j.customer_phone || j.customer_postcode) && (
                  <div className="mt-2 flex gap-2 flex-wrap text-xs">
                    {j.customer_phone && (
                      <a href={`tel:${j.customer_phone}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`call-${j.id}`}>
                        <Phone className="h-3 w-3" /> Call
                      </a>
                    )}
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((j.customer_address || "") + " " + (j.customer_postcode || ""))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:bg-muted" data-testid={`nav-${j.id}`}>
                      <MapPin className="h-3 w-3" /> Navigate
                    </a>
                  </div>
                )}
                {j.status === "scheduled" && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    <button onClick={() => act(j.id, () => api.post(`/jobs/${j.id}/complete`), "Completed · invoice sent")} className="min-h-[44px] rounded-md bg-emerald-600 text-white text-sm font-medium flex items-center justify-center gap-1" data-testid={`route-complete-${j.id}`}><Check className="h-4 w-4" /> Done</button>
                    <button onClick={() => act(j.id, () => api.post(`/jobs/${j.id}/no-access`), "Marked no access")} className="min-h-[44px] rounded-md bg-rose-600 text-white text-sm font-medium flex items-center justify-center gap-1" data-testid={`route-noaccess-${j.id}`}><X className="h-4 w-4" /> No access</button>
                    <button onClick={async () => { const d = window.prompt("New date YYYY-MM-DD", todayISO()); if (d) act(j.id, () => api.post(`/jobs/${j.id}/reschedule`, { scheduled_date: d }), "Rescheduled"); }} className="min-h-[44px] rounded-md border border-border text-sm flex items-center justify-center gap-1" data-testid={`route-reschedule-${j.id}`}><RotateCw className="h-4 w-4" /> Resch.</button>
                    <button onClick={() => act(j.id, () => api.post(`/jobs/${j.id}/mark-cash`), "Paid in cash")} className="min-h-[44px] rounded-md border border-emerald-600 text-emerald-700 text-sm flex items-center justify-center gap-1" data-testid={`route-cash-${j.id}`}><CircleDollarSign className="h-4 w-4" /> Cash</button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatCell({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 text-center">
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
